import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { StoreConfigProvider } from "@/contexts/StoreConfigContext";
import { AuthProvider as AdminAuthProvider, useAuth } from "@/hooks/useAuth";
import { useMarketingAttribution } from "@/hooks/useMarketingAttribution";
import { AdminOrderListener } from "@/components/admin/AdminOrderListener";
import { AdminKeyboardShortcut } from "@/components/admin/AdminKeyboardShortcut";
import { AnnouncementModal } from "@/components/layout/AnnouncementModal";
import { CookieConsentBanner } from "@/components/layout/CookieConsentBanner";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { ChunkLoadErrorBoundary } from "@/components/ui/ChunkLoadErrorBoundary";

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home"));
const Builder = lazy(() => import("./pages/Builder"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const OwnerPanel = lazy(() => import("./pages/OwnerPanel"));
const OwnerLogin = lazy(() => import("./pages/OwnerLogin"));
const ClubPage = lazy(() => import("./pages/ClubPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const OrderDetailsPage = lazy(() => import("./pages/OrderDetailsPage"));
const OrderHistoryPage = lazy(() => import("./pages/OrderHistoryPage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ShopProductPage = lazy(() => import("./pages/ShopProductPage"));
const ShopCheckoutPage = lazy(() => import("./pages/ShopCheckoutPage"));
const ShopOrderSuccessPage = lazy(() => import("./pages/ShopOrderSuccessPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminSyncImages = lazy(() => import("./pages/AdminSyncImages"));
const SeoPreviewPage = lazy(() => import("./pages/SeoPreviewPage"));

// SEO city landing pages (dynamic)
const CityLandingPage = lazy(() => import("./pages/delivery/CityLandingPage"));

import { CustomerChatWidget } from "./components/chat/CustomerChatWidget";
import { AccessibilityWidget } from "./components/accessibility/AccessibilityWidget";

const queryClient = new QueryClient();

// Component that runs the marketing attribution hook globally
function MarketingAttributionCapture() {
  useMarketingAttribution();
  return null;
}

// Global admin listener - only renders for admin users
function GlobalAdminOrderListener() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return null;
  return <AdminOrderListener />;
}

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <StoreConfigProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AnnouncementModal />
                <MarketingAttributionCapture />
                <CartDrawer />
                <ChunkLoadErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/builder" element={<Builder />} />
                      <Route path="/auth" element={<AuthPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/admin" element={<AdminPanel />} />
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin/orders/:orderId" element={<OrderDetailsPage />} />
                      <Route path="/owner" element={<OwnerPanel />} />
                      <Route path="/owner/login" element={<OwnerLogin />} />
                      <Route path="/club" element={<ClubPage />} />
                      <Route path="/my-account" element={<MyAccountPage />} />
                      <Route path="/shop" element={<ShopPage />} />
                      <Route path="/seo-preview" element={<SeoPreviewPage />} />
                      <Route path="/checkout" element={<Navigate to="/shop/checkout" replace />} />
                      <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
                      <Route path="/shop/success" element={<ShopOrderSuccessPage />} />
                      <Route path="/shop/:slug" element={<ShopProductPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/order/success" element={<OrderSuccessPage />} />
                      <Route path="/order/history" element={<OrderHistoryPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/accessibility" element={<AccessibilityPage />} />
                      <Route path="/admin/sync-images" element={<AdminSyncImages />} />

                      <Route path="/delivery/:citySlug" element={<CityLandingPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ChunkLoadErrorBoundary>
                <AdminKeyboardShortcut />
                <GlobalAdminOrderListener />
                <AccessibilityWidget />
                <CustomerChatWidget />
                <CookieConsentBanner />
              </BrowserRouter>
            </TooltipProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </StoreConfigProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
